// reportAssistantBridge — module-level singleton that lets the action-proposal
// executor registry (`builtinActions.ts`, a plain module that runs at import
// time and cannot use hooks) hand an iterate instruction to the report page.
//
// `requestIterate(...)` is LISTENER-based, not mount-based: ReportView is
// keyed on the route's :publicId (App.tsx KeyedReportView), so navigating to
// the URL the user is already on ("update THIS report" from the assistant
// drawer) does NOT remount it, and a mount-time effect would never fire.
// ReportView subscribes while mounted and ALSO drains any request that
// arrived before it mounted (the cross-page case).
//
// The instruction travels in memory only — never as a URL parameter. A
// prefill-and-submit deep link would be a forgeable URL that fires authoring
// runs (cost) for any logged-in visitor; see handoff.py for the same
// reasoning on the data-chat handoff.
//
// (The creation half, which opened the old create-report drawer, went with
// the program report, ADR-0495: the assistant's create card now creates a
// block report directly.)

// ---------------------------------------------------------------------------
// Iterate handoff
// ---------------------------------------------------------------------------

export interface IterateRequest {
  reportId: string;
  instruction: string;
  targetSectionIds: string[] | null;
}

type IterateListener = (request: IterateRequest) => void;

let pendingIterate: IterateRequest | null = null;
const iterateListeners = new Set<IterateListener>();

/**
 * Hand an instruction to the report page for `request.reportId`. If a
 * ReportView for that report is mounted and listening it receives the request
 * immediately; otherwise the request is parked until one mounts and drains it
 * (`consumePendingIterate`). A newer request replaces an older parked one —
 * there is only ever one pending handoff.
 */
export function requestIterate(request: IterateRequest): void {
  pendingIterate = request;
  for (const listener of iterateListeners) {
    listener(request);
  }
}

/**
 * Subscribe to iterate requests. Returns the unsubscribe function. Listeners
 * are responsible for calling `consumePendingIterate` once they have taken a
 * request, so a request the listener ignored (wrong report) stays parked.
 */
export function subscribeIterate(listener: IterateListener): () => void {
  iterateListeners.add(listener);
  return () => {
    iterateListeners.delete(listener);
  };
}

/**
 * Take the parked request for `reportId` (consume-once). Returns `null` when
 * nothing is parked or the parked request is for a different report — that
 * one stays parked for its own page.
 */
export function consumePendingIterate(reportId: string): IterateRequest | null {
  if (!pendingIterate || pendingIterate.reportId !== reportId) return null;
  const taken = pendingIterate;
  pendingIterate = null;
  return taken;
}

/** Drop a parked request (e.g. the page decided the user may not edit). */
export function clearPendingIterate(reportId: string): void {
  if (pendingIterate && pendingIterate.reportId === reportId) {
    pendingIterate = null;
  }
}

/** Test-only: reset module state between cases. */
export function _resetReportAssistantBridgeForTests(): void {
  pendingIterate = null;
  iterateListeners.clear();
}
